// For v2 of the seismic submittal tool
import axios from 'axios';
import ShortUniqueId from 'short-unique-id';
import Vue from 'vue';
import { debounce } from 'lodash';

const randomUUID = (new ShortUniqueId()).randomUUID;

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

const add = nums => {
    if (nums.includes(null) || nums.includes(undefined)) {
        return null;
    }

    return nums.reduce(
        (r, i) => r + parseFloat(i), 0
    );
};

const createUUID = () => randomUUID(4);

export default {
    namespaced: true,

    strict: false,

    state: {
        id: null,
        design: {},
        fetched: false,
        cp: null,
        orientation: 'lateral',
        // I don't think this is used anywhere
        weight: null,
        branches: [],
        segments: [],
        errors: {
            maxLoad: false
        },
        nfpaSystemMultiplier: 1.15,
        maxFpw: null,
        totalZoiSystemWeight: null,
        zoiSystemDesignWeightWp: null,
        zoiSeismicDesignLoad: null,
        mainPipeType: null,
        systemMaterial: null,
    },
    getters: {
        state(state, getters) {
            return state;
        },
        braceId(state) {
            return state.id;
        },
        branch(state) {
            return branchUid => state.branches.find(i => i.uid === branchUid);
        },
        branchIndex(state) {
            return branchUid => state.branches.findIndex(i => i.uid === branchUid);
        },
        mainBranch(state) {
            return state.branches[0];
        },
        orientation(state) {
            return state.orientation;
        },
        normalBranches(state) {
            return state.branches.slice(1);
        },
        mainBranchSegments(state, getters) {
            if (getters.mainBranch) {
                return getters.segments(getters.mainBranch.segments);
            }

            return [];
        },
        mainTotalLengths(state, getters) {
            let totalLateralLength = 0;
            let totalVerticalLength = 0;
            let totalLongitudinalLength = 0;

            getters.mainBranchSegments.forEach(s => {
                totalLateralLength += parseFloat(s.lateralPipeLength);
                totalVerticalLength += parseFloat(s.verticalPipeLength);
                totalLongitudinalLength += parseFloat(s.longitudinalPipeLength);
            });

            return {
                lateral: totalLateralLength,
                vertical: totalVerticalLength,
                longitudinal: totalLongitudinalLength,
            };
        },
        mainBranchLengthErrors(state, getters) {
            let lengths = getters.mainTotalLengths;
            let errors = [];

            if (['lateral', 'lateral-longitudinal', 'longitudinal-lateral'].includes(state.orientation) && lengths.lateral > 40) {
                errors.push('Total Lateral Pipe Length must be between 0ft and 40ft (0m and 12.1m)');
            }
            if (['longitudinal', 'lateral-longitudinal', 'longitudinal-lateral'].includes(state.orientation) && lengths.longitudinal > 80) {
                errors.push('Total Longitudinal Pipe Length must be between 0ft and 80ft (0m and 24.3m)');
            }
            if (['riser-wall', 'riser-corner'].includes(state.orientation) && lengths.vertical > 25) {
                errors.push('Total Vertical Pipe Length must be between 0ft and 25ft (0m and 7.6m)');
            }

            return errors;
        },
        mainTotalLength(state, getters) {
            let l = getters.mainBranchSegments.filter(s => {
                return s.lateralPipeLength !== null || s.verticalPipeLength !== null;
            }).reduce((a,b) => {
                if (['lateral', 'lateral-longitudinal', 'longitudinal-lateral'].includes(state.orientation)) {
                    return b.lateralPipeLength ? a + b.lateralPipeLength : a;
                } else if (['riser-wall', 'riser-corner'].includes(state.orientation)) {
                    return b.verticalPipeLength ? a + b.verticalPipeLength : a;
                } else {
                    return a; // why doesn't this use longitudinal?
                }
            }, 0);

            return l ? parseFloat(l).toFixed(2) : 0;
        },
        largestMainBranchPipeSize(state, getters) {
            if (!getters.mainBranch) {
                return null;
            }

            return getters.mainBranchSegments.reduce((a,b) => b ? Math.max(a, b.pipeSize) : 0, 0);
        },
        largestMainBranchPipeSizeAndType(state, getters) {
            let largest = {
                pipeName: null,
                pipeSize: null,
                pipeType: null
            };

            if (!getters.mainBranch) {
                return largest;
            }

            getters.mainBranchSegments.forEach((s) => {
                if (!largest.pipeSize || largest.pipeSize < s.pipeSize) {
                    largest.pipeName = s.pipeName;
                    largest.pipeSize = s.pipeSize;
                    largest.pipeType = s.pipeType;
                }
            });

            return largest;
        },
        mainPipeSize(state, getters) {
            let segments = getters.mainBranchSegments;

            if (segments[0]) {
                return segments[0].pipeSize || null;
            }

            return null;
        },
        branchFromSegment(state, getters) {
            return (segmentUid) => {
                for (let i in state.branches) {
                    if (state.branches[i].segments.some(s => s === segmentUid)) {
                        return state.branches[i];
                    }
                }
            };
        },
        compactBranches(state, getters) {
            let branches = JSON.parse(JSON.stringify(state.branches));

            branches.forEach(b => {
                b.segments = getters.segments(b.segments);
            });

            return branches;
        },
        segment(state, getters) {
            return segmentUid => {
                return state.segments.find(i => i.uid == segmentUid);
            };
        },
        segments(state, getters) {
            return (segmentUids) => {
                return segmentUids.map(uid => getters.segment(uid));
            };
        },
        segmentIndex(state, getters) {
            return segmentUid => {
                return getters.branchFromSegment(segmentUid).segments.findIndex(i => i === segmentUid);
            };
        },
        segmentSiblings(state, getters) {
            return (segmentUid) => {
                return getters.segments(getters.branchFromSegment(segmentUid).segments);
            };
        },
        normalSegmentUids(state, getters) {
            return branchUid => {
                return getters.segments(getters.branch(branchUid).segments).filter(i => i.riserNipple !== true).map(i => i.uid);
            };
        },
        riserNippleUid(state, getters) {
            return branchUid => {
                return getters.segments(getters.branch(branchUid).segments).filter(i => i.riserNipple === true).map(i => i.uid).shift();
            };
        },
        riserNippleSegment(state, getters) {
            return branchUid => {
                return getters.segment(getters.riserNippleUid(branchUid));
            }
        },
        hasRiserNippleErrors(state, getters) {
            return (state.branches.find(i => i.riserNippleError === true) !== undefined);
        },
        mainSegmentUid(state, getters) {
            return branchUid => {
                return getters.normalSegmentUids(branchUid)[0];
            };
        },
        supplementarySegmentUids(state, getters) {
            return branchUid => {
                return getters.normalSegmentUids(branchUid).slice(1);
            }
        },
        segmentUids(state, getters) {
            return branchUid => {
                return getters.riserNippleUid(branchUid)
                    ? [...getters.normalSegmentUids(branchUid), getters.riserNippleUid(branchUid)]
                    : getters.normalSegmentUids(branchUid);
            };
        },
        pipes(state, getters, root) {
            return root.submittalPipes;
        },
        zoiSeismicDesignLoad(state) {
            return state.zoiSeismicDesignLoad;
        },
        maxFpw(state) {
            return state.maxFpw;
        },
        systemErrors(state) {
            return state.errors;
        },
        mainPipeType(state) {
            return state.mainPipeType;
        },
        systemMaterial(state) {
            return state.systemMaterial;
        },
    },
    actions: {
        setOrientation({commit, dispatch, rootGetters}, payload) {
            commit('setOrientation', payload);
            dispatch('resetSegments');
            commit('calculateWeights', { rootGetters });
        },
        setCp({commit, rootGetters}, payload) {
            commit('setCp', payload);
            commit('calculateWeights', { rootGetters });
        },
        fetchMaxLoadError({state, commit, getters}) {
            let url = `/actions/seismic-submittal/pipe/show-max-load-error`;

            let sgmts = getters.mainBranchSegments.map(s => {
                    if (!s.pipeName || !s.pipeSize || !s.pipeType) {
                        return false;
                    }
                    return { pipeName: s.pipeName, pipeSize: s.pipeSize, pipeType: s.pipeType };
                });

            return axios.post(url, {
                length: getters.mainTotalLength,
                segments: sgmts,
                fpw: getters.zoiSeismicDesignLoad,
                standardId: state.design.standardId,
            }).then(r => commit('setMaxLoadError', r.data));
        },
        fetchMaxFpw: debounce(({commit, getters, rootGetters, rootState}) => {
            let system = rootState.submittalSystem;
            let brace = rootState.submittalBrace.records[system.id];
            let material = (brace && brace.systemMaterial) ? brace.systemMaterial : 'Carbon Steel';

            let url = `/actions/seismic-submittal/pipe/get-max-fpw-load`;

            let sgmts = getters.mainBranchSegments.map(s => {
                    if (!s.pipeName || !s.pipeSize || !s.pipeType) {
                        return false;
                    }
                    return { pipeName: s.pipeName, pipeSize: s.pipeSize, pipeType: s.pipeType };
                });

            return axios.post(url, {
                sid: rootGetters['submittalStandards/id'],
                systemMaterial: material,
                length: getters.mainTotalLength,
                segments: sgmts,
            })
                .then(r => commit('setMaxFpw', r.data))
                .then(function(){ commit('calculateWeights', { rootGetters }) });
        }, 200),

        fetchRiserNippleCalculation: debounce(({state, commit, getters}) => {
            let url = `/actions/seismic-submittal/pipe/riser-nipple-calculation`;

            getters.normalBranches.forEach(branch => {
                commit('setBranch', { getters, branch, data: { riserNippleError: false }});

                branch.riserNippleError = false

                let riserNipple = getters.riserNippleSegment(branch.uid);

                if (riserNipple) {
                    let params = {
                        standardId: state.design.standardId,
                        size: riserNipple.pipeSize,
                        pipeType: riserNipple.pipeType,
                        length: riserNipple.pipeLength,
                        branchWeight: branch.weight,
                        cp: getters.state.cp
                    };

                    for (let i in params) {
                        if (([null, undefined]).includes(params[i])) {
                            return;
                        }
                    }

                    axios.get(url, { params })
                        .then((response) => {
                            commit('setBranch', { getters, branch, data: { riserNippleError: response.data.error, riserNippleCalculation : response.data }});
                        });
                }
            });
        }, 250),
        fetchAll({dispatch, commit, getters, rootGetters, state}, payload) {
            if (!payload.id) {
                // no id, reset the system state
                commit('resetState');
                return;
            }

            let url = `/actions/seismic-submittal/brace/piping-system?id=${payload.id}`;

            if (state.id !== null && payload.id != state.id) {
                commit('reset');
            }

            commit('setId', payload.id);

            if (state.fetched === true) {
                return Promise.resolve();
            }

            return axios.get(url).then(({data:{data}}) => {
                this.dispatch('submittalStandards/setIdFromRecord', data.design.id);
                
                data.branches.forEach((branch) => {
                    branch.uid = createUUID();
                    commit('addBranch', { data: branch });
                    branch = getters.branch(branch.uid);

                    data.segments.filter(s => s.branchId === branch.id).forEach((segment) => {
                        if (['lateral-longitudinal', 'longitudinal-lateral'].includes(data.brace.orientation)) {
                            if (segment.longitudinalPipeLength) {
                                // Set null values for backward compatibility 
                                if (!segment.secondaryPipeName) {
                                    segment.secondaryPipeName = segment.pipeName;
                                }
                                if (!segment.secondaryPipeSize) {
                                    segment.secondaryPipeSize = Number(segment.pipeSize);
                                }
                                if (!segment.secondaryPipeType) {
                                    segment.secondaryPipeType = segment.pipeType;
                                }
                                if (!segment.secondaryWeightPerFoot) {
                                    segment.secondaryWeightPerFoot = segment.weightPerFoot;
                                }
                            }
                        }
                        segment.riserNipple = !!(parseInt(segment.riserNipple));
                        segment.pipeSize = parseFloat(segment.pipeSize);
                        commit('addSegment', { branch, data: segment, getters });
                    });
                });

                if (state.branches.length == 0) {
                    dispatch('addBranch', {});
                }

                commit('setBrace', data.brace);
                commit('setDesign', data.design);
                commit('setFetched', true);
                commit('calculateWeights', { rootGetters });
            }).then((response) => {
                dispatch('fetchMaxFpw');
            });
        },
        reset({commit}) {
            commit('reset');
        },
        calculateWeights: ({commit, rootGetters}) => {
            commit('calculateWeights', { rootGetters });
        },
        cloneBrace({}, id) {
            let url = `/actions/seismic-submittal/brace/copy`;

            return axios.post(url, {id: id});
        },
        addBranch({dispatch, commit, rootGetters}, payload = {}) {
            let data = payload.data || {};

            data.uid = createUUID();

            commit('addBranch', { data });
            dispatch('addSegment', {uid: data.uid });
            dispatch('fetchMaxFpw');
            commit('calculateWeights', { rootGetters });
            return data.uid;
        },
        cloneBranch({dispatch, commit, getters, rootGetters}, payload) {
            let branch = JSON.parse(JSON.stringify(getters.branch(payload.uid)));
            let segments = JSON.parse(JSON.stringify(getters.segments(branch.segments)));

            branch.uid = createUUID();
            branch.segments = [];

            commit('addBranch', { data: branch });

            segments.forEach(s => {
                s.uid = createUUID();
                commit('addSegment', { branch, data: s, getters });
            });

            dispatch('fetchMaxFpw');
            commit('calculateWeights', { rootGetters });
        },
        setBranch({dispatch, commit, getters, rootGetters}, payload) {
            let branch = getters.branch(payload.uid);

            commit('setBranch', { getters, branch, data: payload });
            dispatch('fetchMaxFpw');
            commit('calculateWeights', { rootGetters });
        },
        removeBranch({dispatch, commit, rootGetters}, payload) {
            commit('removeBranch', payload.uid);
            dispatch('fetchMaxFpw');
            commit('calculateWeights', { rootGetters });
        },
        addSegment({dispatch, commit, getters, rootGetters}, payload = {}) {
            let branchUid = payload.uid;
            let branch = getters.branch(branchUid);
            let data = payload.data || {};

            data.uid = createUUID();

            commit('addSegment', { branch, data, getters });
            dispatch('fetchMaxFpw');
            commit('calculateWeights', { rootGetters });

            return data.uid;
        },
        setSegment({dispatch, commit, getters, rootGetters}, payload) {
            let segment = getters.segment(payload.uid);
            let branch = getters.branchFromSegment(payload.uid);

            commit('setSegment', { getters, branch, segment, data: payload });
            dispatch('fetchMaxFpw');
            commit('calculateWeights', { rootGetters });
            dispatch('fetchRiserNippleCalculation');
        },
        removeSegment({dispatch, commit, getters, rootGetters}, payload) {
            commit('removeSegment', { getters, uid: payload.uid });
            dispatch('fetchMaxFpw');
            commit('calculateWeights', { rootGetters });
        },
        resetSegments({state, commit}) {
            let d = {};
            state.segments.forEach((s) => {
                if (0 == s.sortOrder) {
                    s.pipeLength = null;
                    s.riserNipple = false;
                }

                if ('lateral' == state.orientation) {
                    if (s.sortOrder > 0) {
                        s.lateralPipeLength = null;
                    }
                    
                    s.longitudinalPipeLength = null;
                    s.verticalPipeLength = null;
                    s.secondaryPipeName = null;
                    s.secondaryPipeSize = null;
                    s.secondaryPipeType = null;
                    s.secondaryWeightPerFoot = null;
                }
                if ('longitudinal' == state.orientation) {
                    if (s.sortOrder > 0) {
                        s.longitudinalPipeLength = null;
                    }
                    s.lateralPipeLength = null;
                    s.verticalPipeLength = null;
                    s.secondaryPipeName = null;
                    s.secondaryPipeSize = null;
                    s.secondaryPipeType = null;
                    s.secondaryWeightPerFoot = null;
                }
                if (['lateral-longitudinal','longitudinal-lateral'].includes(state.orientation)) {
                    s.verticalPipeLength = null;
                }
                if (['riser-corner','riser-wall'].includes(state.orientation)) {
                    s.lateralPipeLength = null;
                    s.longitudinalPipeLength = null;
                    s.secondaryPipeName = null;
                    s.secondaryPipeSize = null;
                    s.secondaryPipeType = null;
                    s.secondaryWeightPerFoot = null;
                }

                d = {
                    pipeLength: s.pipeLength,
                    lateralPipeLength: s.lateralPipeLength,
                    longitudinalPipeLength: s.longitudinalPipeLength,
                    verticalPipeLength: s.verticalPipeLength
                }

                commit('setSegment', {segment: s, data: d})
            });
        },
        setMainPipeType({commit}, payload) {
            commit('setMainPipeType', payload.type);

            let url = `/actions/seismic-submittal/pipe/get-system-material`;

            let params = {
                name: payload.name,
                type: payload.type
            };

            axios.get(url, { params })
                .then(r => commit('setSystemMaterial', r.data));
        }
    },
    mutations: {
        reset(state) {
            state.design = {};
            state.fetched = false;
            state.cp = null;
            state.orientation = 'lateral';
            state.branches = [];
            state.segments = [];
            state.errors = { maxLoad: false }
            state.maxFpw = null;
            state.totalZoiSystemWeight = null;
            state.zoiSystemDesignWeightWp = null;
            state.zoiSeismicDesignLoad = null;
            state.mainPipeType = null;
            state.systemMaterial = null;
        },
        setId(state, payload) {
            Vue.set(state, 'id', payload);
        },
        setMaxLoadError(state, payload) {
            Vue.set(state.errors, 'maxLoad', payload);
        },
        setMaxFpw(state, payload) {
            Vue.set(state, 'maxFpw', payload);
        },
        setMainPipeType(state, payload) {
            Vue.set(state, 'mainPipeType', payload);
        },
        setSystemMaterial(state, payload) {
            Vue.set(state, 'systemMaterial', payload);
        },
        setOrientation(state, payload) {
            state.orientation = payload;
        },
        setCp(state, payload) {
            state.cp = payload;
        },
        setFetched(state, payload) {
            state.fetched = payload;
        },
        setDesign(state, payload) {
            Vue.set(state, 'design', payload);
        },
        setBrace(state, payload) {
            state.cp = payload.cp;
            state.orientation = payload.orientation;
            state.totalZoiSystemWeight = payload.totalZoiSystemWeight;
            state.zoiSystemDesignWeightWp = payload.zoiSystemDesignWeightWp;
            state.zoiSeismicDesignLoad = payload.zoiSystemDesignLoad;
        },
        addBranch(state, payload = { data: {} }) {
            state.branches.push({
                uid: randomUUID(4),
                copies: 1,
                segments: [],
                ...payload.data
            });
        },
        setBranch(state, { branch, getters, data }) {
            branch = Object.assign({}, branch, data);
            Vue.set(state.branches, getters.branchIndex(branch.uid), branch);
        },
        removeBranch(state, uid) {
            state.branches.splice(state.branches.findIndex(i => i.uid === uid), 1);
        },
        addSegment(state, payload) {
            // explicitly do not trigger Vue reativity
            state.segments.push(payload.data);

            if (!payload.data.riserNipple) {
                let last = payload.branch.segments.slice(-1)[0];
                if (last && payload.getters.segment(last).riserNipple) {
                    payload.branch.segments.splice(-1, 0, payload.data.uid);
                    state.segments.splice(state.segments.length);
                    return;
                }
            }

            payload.branch.segments.push(payload.data.uid);
            state.segments.splice(state.segments.length);
        },
        setSegment(state, { segment, data }) {
            let index = state.segments.findIndex(i  => i.uid === segment.uid);

            let formatted = {};
            ['pipeLength', 'lateralPipeLength', 'longitudinalPipeLength', 'verticalPipeLength', 'weightPerFoot', 'secondaryWeightPerFoot'].forEach(p => {
                formatted[p] = !isNaN(parseFloat(data[p])) ? parseFloat(data[p]) : null;
            });

            Vue.set(state.segments, index, Object.assign({}, segment, data, formatted));
        },
        removeSegment(state, { getters, uid }) {
            state.segments.splice(state.segments.findIndex(i => i.uid === uid), 1);
            getters.branchFromSegment(uid).segments.splice(getters.segmentIndex(uid), 1);
        },
        resetState(state) {
            state.fetched = false;
            state.cp = null;
            state.orientation = 'lateral';
            state.weight = null,
            state.branches = [],
            state.segments = [],
            state.errors.maxLoad = false;
            state.maxFpw = null;
            state.totalZoiSystemWeight = null;
            state.zoiSystemDesignWeightWp = null;
            state.zoiSeismicDesignLoad = null;
        },
        calculateWeights(state, { rootGetters }) {
            state.errors = {};

            let orientation = state.orientation;
            let pipesLoaded = rootGetters['submittalPipes/loaded'];
            let findPipeFromSizeAndType = rootGetters['submittalPipes/pipeFromSizeAndType'];
            let i = 0;

            state.branches.forEach(b => {
                b.weight = 0;
                b.totalWeight = 0;

                rootGetters['submittalSystem/segments'](b.segments).forEach(s => {
                    i = i + 1;

                    // find pipe weightPerFoot
                    if (!pipesLoaded || !s.pipeSize || !s.pipeType) {
                        s.weightPerFoot = null;
                    }

                    let pipe = findPipeFromSizeAndType(s.pipeSize, s.pipeType);

                    if (pipe) {
                        s.weightPerFoot = pipe.weight;
                    }

                    // set secondary pipe weight
                    pipe = findPipeFromSizeAndType(s.secondaryPipeSize, s.secondaryPipeType);
                    if (pipe) {
                        s.secondaryWeightPerFoot = pipe.weight;
                    }

                    if (!s.weightPerFoot && !s.secondaryWeightPerFoot) {
                        s.weight = null;
                    } else if (!s.mainBranch && s.pipeLength) {
                        s.weight = s.pipeLength * s.weightPerFoot;
                    } else if (orientation === 'lateral' && s.lateralPipeLength !== null) {
                        s.weight = s.lateralPipeLength * s.weightPerFoot;
                    } else if (orientation === 'longitudinal' && s.longitudinalPipeLength !== null) {
                        s.weight = s.longitudinalPipeLength * s.weightPerFoot;
                    } else if (['lateral-longitudinal','longitudinal-lateral'].includes(orientation) && (s.lateralPipeLength !== null || s.longitudinalPipeLength !== null)) {
                        let weight = null;
                        if (s.lateralPipeLength && s.weightPerFoot) {
                            weight += s.lateralPipeLength * s.weightPerFoot;
                        }
                        if (s.longitudinalPipeLength && s.secondaryWeightPerFoot) {
                            weight += s.longitudinalPipeLength * s.secondaryWeightPerFoot;
                        }

                        s.weight = weight;
                    } else if (['riser-wall', 'riser-corner'].includes(orientation) && s.verticalPipeLength !== null) {
                        s.weight = s.verticalPipeLength * s.weightPerFoot;
                    } else {
                        s.weight = null;
                    }

                    if (s.weight === null) {
                        // if any segment weight is null, set branch weight to null
                        b.weight = null;
                        return false; // Break inner forEach loop
                    }
                    if (typeof s.weight === 'number') {
                        b.weight += s.weight;
                        s.weight = s.weight.toFixed(2);
                    }
                });

                if (b.weight === null) {
                    // if any branch weight is null, set branch totalWeight to null
                    b.totalWeight = null;
                    return false; // Break outer forEach loop
                }

                b.totalWeight = (b.weight * b.copies).toFixed(2);
                b.weight = b.weight.toFixed(2);
            });

            state.totalZoiSystemWeight = add(state.branches.map(b => b.totalWeight));
            state.zoiSystemDesignWeightWp = state.totalZoiSystemWeight ? Math.ceil(state.totalZoiSystemWeight * state.nfpaSystemMultiplier) : null;
            state.zoiSeismicDesignLoad = state.totalZoiSystemWeight ? state.zoiSystemDesignWeightWp * state.cp : null;

            if (state.zoiSeismicDesignLoad) {
                if (parseInt(state.design.useMetric)) {
                    state.zoiSeismicDesignLoad = state.zoiSeismicDesignLoad.toFixed(2);
                } else {
                    state.zoiSeismicDesignLoad =  Math.ceil(state.zoiSeismicDesignLoad);
                }
            }

            if (state.totalZoiSystemWeight) {
                state.totalZoiSystemWeight = state.totalZoiSystemWeight.toFixed(2);
            }
            if (state.zoiSeismicDesignLoad && state.maxFpw) {
                state.errors.maxLoad = (parseFloat(state.zoiSeismicDesignLoad) > parseFloat(state.maxFpw));
            } 
        },
    },
};